import * as React from "react";
import axios, { AxiosResponse } from "axios";
import {
  MetricsType,
  ComposedMetricsSite,
  Metrics,
  SummaryRequestSuccess,
  MetricsByType,
} from "./types";
import { useDispatch } from "./";
import { TimeFrame, Sorting } from "../types";

export function useActions() {
  const dispatch = useDispatch();

  /**
   * Retrieves the summary action counts for the workflow
   * performance view.
   */
  const getSummary = React.useCallback(async function getSummary(
    metricsType: MetricsType,
    sites: Map<string, ComposedMetricsSite>,
    startDate: string,
    endDate: string
  ) {
    dispatch({ type: "LOAD_SUMMARY" });
    try {
      const siteIds = Array.from(sites.keys());
      const { data }: AxiosResponse<SummaryRequestSuccess> = await axios.post(
        "/v1/metrics/actions/summary",
        {
          siteIds,
          startDate,
          endDate,
        }
      );
      dispatch({
        type: "SUMMARY_REQUEST_SUCCESS",
        ...data,
        sites: siteIds,
        metricsType,
      });
    } catch (error) {
      // @TODO: Dispatch error state
      // When a site is unable to return the requested data, an error is thrown.
      // This seems to be triggered when a site has no 'closed' actions.
      // Worth investigating a better way to handle this.
    }
  },
  []);

  /**
   * Retrieves the detailed metrics data for the workflow
   * charts.  Includes both by Team and by Site.
   */
  const getMetrics = React.useCallback(async function getMetrics(
    metricsType: MetricsType,
    sites: Set<string>,
    startDate: string,
    endDate: string
  ) {
    dispatch({ type: "LOAD_METRICS" });
    try {
      const siteIds = Array.from(sites);
      const url = `/v1/metrics/actions/${
        metricsType === MetricsType.Opened ? `opened` : `completed`
      }`;
      const { data }: AxiosResponse<Metrics> = await axios.post(url, {
        siteIds,
        startDate,
        endDate,
      });
      dispatch({ type: "METRICS_REQUEST_SUCCESS", metricsType, ...data });
    } catch (error) {
      // @TODO: Dispatch error state
      // When a site is unable to return the requested data, an error is thrown.
      // This seems to be triggered when a site has no 'closed' actions.
      // Worth investigating a better way to handle this.
    }
  },
  []);

  /**
   * Update selected time range for workflow metrics
   */
  const setTimeFrame = React.useCallback(function setTimeFrame(
    timeFrameOption: TimeFrame,
    startDate: string,
    endDate: string
  ) {
    dispatch({ type: "SET_TIME_FRAME", startDate, endDate, timeFrameOption });
  },
  []);

  /**
   * Set selected sites for workflow metrics
   */
  const setSites = React.useCallback(function setSites(
    sites: ComposedMetricsSite[]
  ) {
    dispatch({ type: "SET_SITES_LIST", sites });
  },
  []);

  /**
   * Set the byType (sites || responsibility)
   */
  const setByType = React.useCallback(function setByType(by: MetricsByType) {
    dispatch({ type: "SET_METRICS_BY_TYPE", by });
  }, []);

  /**
   * Set ordering of chart bars (most to least || least to most)
   */
  const setSortDirection = React.useCallback(function setSortDirection(
    value: Sorting
  ) {
    dispatch({ type: "SET_SORTING_DIRECTION", sortingDirection: value });
  },
  []);

  return {
    getSummary,
    getMetrics,
    setTimeFrame,
    setSites,
    setByType,
    setSortDirection,
  };
}
